import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleClearCredentials = () => {
    localStorage.removeItem('apiKey');
    localStorage.removeItem('apiHostname');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="app app-setup">
          <header className="app-header">
            <div className="brand-text">
              <h1 className="brand-name">Form Pipeline Manager</h1>
              <span className="brand-eyebrow">An error occurred</span>
            </div>
          </header>
          <main className="setup-page">
            <div className="setup-card error-card">
              <h2 className="error-card-title">Something went wrong</h2>
              <p className="error-card-copy">
                The application encountered an unexpected error. This might be due to an API compatibility issue.
              </p>

              <div className="error-card-details">
                <strong>Error Details:</strong>
                <code>
                  {this.state.error?.message || 'Unknown error'}
                </code>
                {this.state.error?.stack && (
                  <pre>
                    {this.state.error.stack}
                  </pre>
                )}
              </div>

              <div className="error-card-actions">
                <button className="btn btn-primary" onClick={this.handleReset}>
                  Try Again
                </button>
                <button className="btn btn-secondary" onClick={this.handleClearCredentials}>
                  Clear Credentials & Restart
                </button>
              </div>
            </div>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
